import { inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { environment } from '@environments/environment';
import { CookieService } from 'ngx-cookie-service';
import { filter } from 'rxjs';

/**
 * HubSpot tracking service global declaration.
 * Declares the global HubSpot tracking object `_hsq`.
 * @internal
 */
declare global {
    interface Window {
        _hsq: any[];
    }
}

/**
 * Service for handling HubSpot tracking, including page views and visitor identification.
 *
 * @export
 * @class HubspotTrackingService
 * @typedef {HubspotTrackingService}
 */
@Injectable( {
    providedIn: 'root',
} )
export class HubspotTrackingService {

    #router = inject( Router );
    #cookieService = inject( CookieService );

    #scriptLoaded = false;

    get #hsq(): any[] {
        window._hsq = window._hsq || [];
        return window._hsq;
    }

    get #hasConsent(): boolean {
        return this.#cookieService.get( 'scope.consent' ) === 'true';
    }

    /**
     * Dynamically loads the HubSpot tracking script into the DOM.
     * Call this after the user gives consent.
     * Idempotent — safe to call multiple times.
     */
    loadScript(): void {
        if ( !this.#hasConsent ) return;

        if ( this.#scriptLoaded || document.getElementById( 'hs-script-loader' ) ) {
            return;
        }

        const script = document.createElement( 'script' );
        script.id = 'hs-script-loader';
        script.type = 'text/javascript';
        script.async = true;
        script.defer = true;
        script.src = `//js.hs-scripts.com/${environment.keys.hubspotPortalId}.js`;
        document.head.appendChild( script );

        this.#scriptLoaded = true;
        this.#initRouterTracking();
    }

    /**
     * Subscribes to Router events and tracks page views on every NavigationEnd.
     * Called internally after the script is loaded.
     */
    #initRouterTracking(): void {
        this.#router.events
            .pipe( filter( ( event ) => event instanceof NavigationEnd ) )
            .subscribe( ( event: NavigationEnd ) => this.trackPageView( event.urlAfterRedirects ) );
    }

    /**
     * Tracks a page view manually.
     * @param path Optional path to track; defaults to current path
     */
    trackPageView( path?: string ): void {
        if ( !this.#hasConsent || !this.#scriptLoaded ) {
            return;
        }
        this.#hsq.push( [ 'setPath', path || window.location.pathname ] );
        this.#hsq.push( [ 'trackPageView' ] );
    }

    /**
     * Identifies a visitor by email address in HubSpot.
     * Only call when you know the user's identity (e.g. after login).
     * @param email Email address of the visitor
     */
    identifyVisitor( email: string ): void {
        if ( !this.#hasConsent || !this.#scriptLoaded ) {
            return;
        }
        this.#hsq.push( [ 'identify', { email } ] );
    }

    /**
     * Removes HubSpot cookies and revokes consent.
     * Call when the user withdraws consent.
     */
    removeConsent(): void {
        this.#hsq.push( [ 'revokeCookieConsent' ] );
    }
}
